import { useState, useEffect, useRef } from 'react';
import { Save, Loader2, Plus, X, Upload, Palette, Mail, RefreshCw, Send, Eye, EyeOff, Trash2, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const defaultColors = {
  primary: "#CCFF00",
  secondary: "#7000FF",
  accent: "#00F0FF",
  background: "#030711",
  foreground: "#F8FAFC",
  muted: "#64748B",
  card: "#0F172A",
  border: "#1E293B",
};

const SettingsManager = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [newSocialPlatform, setNewSocialPlatform] = useState('');
  const [newSocialUrl, setNewSocialUrl] = useState('');
  const [newCcEmail, setNewCcEmail] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminApi.getSettings();
      // Ensure colors and smtp objects exist with defaults
      const data = response.data;
      if (!data.colors) data.colors = { ...defaultColors };
      if (!data.smtp) data.smtp = {
        enabled: false,
        host: "valor-smtp.us-east-2.amazonaws.com",
        port: 587,
        username: "",
        password: "",
        from_email: "",
        from_name: "",
        to_email: "",
        to_name: "OQ Technology",
        cc_emails: [],
        use_tls: true,
      };
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  const handleColorChange = (colorKey, value) => {
    setSettings({
      ...settings,
      colors: { ...settings.colors, [colorKey]: value }
    });
  };

  const handleSmtpChange = (key, value) => {
    setSettings({
      ...settings,
      smtp: { ...settings.smtp, [key]: value }
    });
  };

  const handleAddSocialLink = () => {
    if (!newSocialPlatform || !newSocialUrl) return;
    setSettings({
      ...settings,
      social_links: {
        ...settings.social_links,
        [newSocialPlatform.toLowerCase()]: newSocialUrl,
      },
    });
    setNewSocialPlatform('');
    setNewSocialUrl('');
  };

  const handleRemoveSocialLink = (platform) => {
    const newLinks = { ...settings.social_links };
    delete newLinks[platform];
    setSettings({ ...settings, social_links: newLinks });
  };

  const handleAddCcEmail = () => {
    if (!newCcEmail || !newCcEmail.includes('@')) return;
    const currentCc = settings.smtp?.cc_emails || [];
    if (!currentCc.includes(newCcEmail)) {
      handleSmtpChange('cc_emails', [...currentCc, newCcEmail]);
    }
    setNewCcEmail('');
  };

  const handleRemoveCcEmail = (email) => {
    const currentCc = settings.smtp?.cc_emails || [];
    handleSmtpChange('cc_emails', currentCc.filter(e => e !== email));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSettings(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      // First save current settings
      await adminApi.updateSettings(settings);
      const response = await adminApi.testSmtpConnection();
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.error || 'SMTP test failed');
      }
    } catch (error) {
      console.error('SMTP test error:', error);
      toast.error('Failed to test SMTP connection');
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      // First save current settings
      await adminApi.updateSettings(settings);
      const response = await adminApi.sendTestEmail();
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Send test email error:', error);
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const resetColors = () => {
    setSettings({ ...settings, colors: { ...defaultColors } });
    toast.info('Colors reset to defaults');
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const response = await adminApi.uploadImage(file, 'logo');
      if (response.data.success) {
        setSettings({ ...settings, logo_url: response.data.url });
        toast.success('Logo uploaded successfully');
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingFavicon(true);
    try {
      const response = await adminApi.uploadImage(file, 'favicon');
      if (response.data.success) {
        setSettings({ ...settings, favicon_url: response.data.url });
        toast.success('Favicon uploaded successfully');
      }
    } catch (error) {
      console.error('Favicon upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload favicon');
    } finally {
      setUploadingFavicon(false);
      if (faviconInputRef.current) faviconInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (imageType) => {
    try {
      const response = await adminApi.deleteImage(imageType);
      if (response.data.success) {
        if (imageType === 'logo') {
          setSettings({ ...settings, logo_url: null });
        } else {
          setSettings({ ...settings, favicon_url: null });
        }
        toast.success(`${imageType.charAt(0).toUpperCase() + imageType.slice(1)} removed`);
      }
    } catch (error) {
      console.error('Delete image error:', error);
      toast.error('Failed to delete image');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="settings-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">Site Settings</h1>
          <p className="text-muted-foreground">Configure site-wide settings, branding, colors, and email.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Changes
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="bg-white/5 p-1">
          <TabsTrigger value="general" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            General
          </TabsTrigger>
          <TabsTrigger value="branding" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            Branding & Logo
          </TabsTrigger>
          <TabsTrigger value="colors" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Palette className="w-4 h-4 mr-2" />
            Colors
          </TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Mail className="w-4 h-4 mr-2" />
            Email (SMTP)
          </TabsTrigger>
          <TabsTrigger value="social" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            Social Links
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <div className="admin-card space-y-6">
            <h2 className="text-xl font-heading font-semibold">General Information</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="site_name" className="form-label">Site Name</label>
                <Input
                  id="site_name"
                  name="site_name"
                  value={settings?.site_name || ''}
                  onChange={handleChange}
                  placeholder="OQ Technology"
                  className="input-neon"
                  data-testid="settings-name-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="tagline" className="form-label">Tagline</label>
                <Input
                  id="tagline"
                  name="tagline"
                  value={settings?.tagline || ''}
                  onChange={handleChange}
                  placeholder="Building Digital Futures"
                  className="input-neon"
                  data-testid="settings-tagline-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="contact_email" className="form-label">Contact Email</label>
                <Input
                  id="contact_email"
                  name="contact_email"
                  type="email"
                  value={settings?.contact_email || ''}
                  onChange={handleChange}
                  placeholder="hello@oqtechnology.com"
                  className="input-neon"
                  data-testid="settings-email-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact_phone" className="form-label">Contact Phone</label>
                <Input
                  id="contact_phone"
                  name="contact_phone"
                  value={settings?.contact_phone || ''}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  className="input-neon"
                  data-testid="settings-phone-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address" className="form-label">Address</label>
              <Input
                id="address"
                name="address"
                value={settings?.address || ''}
                onChange={handleChange}
                placeholder="123 Tech Street, Silicon Valley, CA"
                className="input-neon"
                data-testid="settings-address-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="footer_text" className="form-label">Footer Text</label>
              <Textarea
                id="footer_text"
                name="footer_text"
                value={settings?.footer_text || ''}
                onChange={handleChange}
                placeholder="© 2024 OQ Technology. All rights reserved."
                rows={2}
                className="input-neon resize-none"
                data-testid="settings-footer-input"
              />
            </div>
          </div>
        </TabsContent>

        {/* Branding & Logo */}
        <TabsContent value="branding" className="space-y-6">
          <div className="admin-card space-y-6">
            <h2 className="text-xl font-heading font-semibold">Logo & Branding</h2>
            
            {/* Logo Section */}
            <div className="p-4 border border-white/10 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <label className="form-label mb-0">Site Logo</label>
                <div className="flex gap-2">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg,.ico,.webp,.gif"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <Button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="btn-primary"
                    data-testid="upload-logo-btn"
                  >
                    {uploadingLogo ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload Logo
                  </Button>
                  {settings?.logo_url && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleDeleteImage('logo')}
                      className="btn-outline text-red-400 hover:text-red-300"
                      data-testid="delete-logo-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="logo_url" className="text-xs text-muted-foreground">Logo URL (or paste external URL)</label>
                <Input
                  id="logo_url"
                  name="logo_url"
                  value={settings?.logo_url || ''}
                  onChange={handleChange}
                  placeholder="https://example.com/logo.png or /assets/logo.png"
                  className="input-neon"
                  data-testid="settings-logo-input"
                />
              </div>

              {settings?.logo_url && (
                <div className="p-4 bg-white/5 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-3">Logo Preview:</p>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="p-4 bg-background rounded-lg">
                      <img 
                        src={settings.logo_url} 
                        alt="Logo preview dark" 
                        className="h-12 max-w-[200px] object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                    <div className="p-4 bg-white rounded-lg">
                      <img 
                        src={settings.logo_url} 
                        alt="Logo preview light" 
                        className="h-12 max-w-[200px] object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  </div>
                </div>
              )}

              {!settings?.logo_url && (
                <div className="p-6 bg-white/5 rounded-lg text-center border-2 border-dashed border-white/10">
                  <Image className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No logo uploaded. Click "Upload Logo" or paste a URL.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Recommended: 200x50px PNG/SVG with transparent background
                  </p>
                </div>
              )}
            </div>

            {/* Favicon Section */}
            <div className="p-4 border border-white/10 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <label className="form-label mb-0">Site Favicon</label>
                <div className="flex gap-2">
                  <input
                    ref={faviconInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg,.ico,.webp,.gif"
                    onChange={handleFaviconUpload}
                    className="hidden"
                    id="favicon-upload"
                  />
                  <Button
                    type="button"
                    onClick={() => faviconInputRef.current?.click()}
                    disabled={uploadingFavicon}
                    className="btn-primary"
                    data-testid="upload-favicon-btn"
                  >
                    {uploadingFavicon ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload Favicon
                  </Button>
                  {settings?.favicon_url && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleDeleteImage('favicon')}
                      className="btn-outline text-red-400 hover:text-red-300"
                      data-testid="delete-favicon-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="favicon_url" className="text-xs text-muted-foreground">Favicon URL (or paste external URL)</label>
                <Input
                  id="favicon_url"
                  name="favicon_url"
                  value={settings?.favicon_url || ''}
                  onChange={handleChange}
                  placeholder="https://example.com/favicon.ico or /assets/favicon.ico"
                  className="input-neon"
                  data-testid="settings-favicon-input"
                />
              </div>

              {settings?.favicon_url && (
                <div className="p-4 bg-white/5 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-3">Favicon Preview:</p>
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-background rounded-lg">
                      <img 
                        src={settings.favicon_url} 
                        alt="Favicon preview" 
                        className="w-8 h-8 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <p>This will appear in browser tabs</p>
                    </div>
                  </div>
                </div>
              )}

              {!settings?.favicon_url && (
                <div className="p-6 bg-white/5 rounded-lg text-center border-2 border-dashed border-white/10">
                  <Image className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No favicon uploaded. Click "Upload Favicon" or paste a URL.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Recommended: 32x32px ICO or PNG file
                  </p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Colors */}
        <TabsContent value="colors" className="space-y-6">
          <div className="admin-card space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-heading font-semibold">Color Management</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Customize all website colors using hex codes. Changes apply globally.
                </p>
              </div>
              <Button variant="outline" onClick={resetColors} className="btn-outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset to Defaults
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {Object.entries(settings?.colors || defaultColors).map(([key, value]) => (
                <div key={key} className="form-group">
                  <label className="form-label capitalize">{key.replace('_', ' ')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={value}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <Input
                      value={value}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      placeholder="#CCFF00"
                      className="input-neon font-mono uppercase"
                      data-testid={`color-${key}-input`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-sm text-muted-foreground mb-4">Color Preview:</p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(settings?.colors || defaultColors).map(([key, value]) => (
                  <div key={key} className="text-center">
                    <div 
                      className="w-16 h-16 rounded-lg border border-white/10 mb-1"
                      style={{ backgroundColor: value }}
                    />
                    <p className="text-xs text-muted-foreground capitalize">{key}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <h4 className="font-semibold mb-2">Color Usage Guide</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li><strong>Primary:</strong> Buttons, active states, highlights, icons</li>
                <li><strong>Secondary:</strong> Secondary buttons, accents</li>
                <li><strong>Accent:</strong> Links, special highlights</li>
                <li><strong>Background:</strong> Page background</li>
                <li><strong>Foreground:</strong> Main text color</li>
                <li><strong>Muted:</strong> Secondary text, placeholders</li>
                <li><strong>Card:</strong> Card backgrounds, panels</li>
                <li><strong>Border:</strong> Borders, dividers</li>
              </ul>
            </div>
          </div>
        </TabsContent>

        {/* Email (SMTP) */}
        <TabsContent value="email" className="space-y-6">
          <div className="admin-card space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-heading font-semibold">Email Configuration (SMTP)</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Configure SMTP to receive contact form submissions via email.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">Enable Email</span>
                <Switch
                  checked={settings?.smtp?.enabled || false}
                  onCheckedChange={(checked) => handleSmtpChange('enabled', checked)}
                  data-testid="smtp-enabled-switch"
                />
              </div>
            </div>

            {settings?.smtp?.enabled && (
              <>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="form-group">
                    <label className="form-label">SMTP Host</label>
                    <Input
                      value={settings?.smtp?.host || ''}
                      onChange={(e) => handleSmtpChange('host', e.target.value)}
                      placeholder="valor-smtp.us-east-2.amazonaws.com"
                      className="input-neon font-mono"
                      data-testid="smtp-host-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">SMTP Port</label>
                    <Input
                      type="number"
                      value={settings?.smtp?.port || 587}
                      onChange={(e) => handleSmtpChange('port', parseInt(e.target.value) || 587)}
                      placeholder="587"
                      className="input-neon"
                      data-testid="smtp-port-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="form-group">
                    <label className="form-label">SMTP Username</label>
                    <Input
                      value={settings?.smtp?.username || ''}
                      onChange={(e) => handleSmtpChange('username', e.target.value)}
                      placeholder="SMTP username"
                      className="input-neon"
                      data-testid="smtp-username-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">SMTP Password</label>
                    <div className="relative">
                      <Input
                        type={showSmtpPassword ? 'text' : 'password'}
                        value={settings?.smtp?.password || ''}
                        onChange={(e) => handleSmtpChange('password', e.target.value)}
                        placeholder="SMTP password"
                        className="input-neon pr-10"
                        data-testid="smtp-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <p className="font-medium">Use TLS</p>
                    <p className="text-sm text-muted-foreground">Enable TLS encryption (recommended)</p>
                  </div>
                  <Switch
                    checked={settings?.smtp?.use_tls ?? true}
                    onCheckedChange={(checked) => handleSmtpChange('use_tls', checked)}
                    data-testid="smtp-tls-switch"
                  />
                </div>

                <hr className="border-white/10" />

                <h3 className="font-heading font-semibold">Sender Configuration</h3>
                
                <div className="form-group">
                  <label className="form-label">From Email (Verified Sender)</label>
                  <Input
                    type="email"
                    value={settings?.smtp?.from_email || ''}
                    onChange={(e) => handleSmtpChange('from_email', e.target.value)}
                    placeholder="noreply@yourdomain.com"
                    className="input-neon"
                    data-testid="smtp-from-email-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    For AWS SES, this must be a verified email address.
                  </p>
                </div>

                <hr className="border-white/10" />

                <h3 className="font-heading font-semibold">Recipient Configuration</h3>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="form-group">
                    <label className="form-label">Recipient Name (Company Name)</label>
                    <Input
                      value={settings?.smtp?.to_name || ''}
                      onChange={(e) => handleSmtpChange('to_name', e.target.value)}
                      placeholder="OQ Technology"
                      className="input-neon"
                      data-testid="smtp-to-name-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Recipient Email (Admin Email)</label>
                    <Input
                      type="email"
                      value={settings?.smtp?.to_email || ''}
                      onChange={(e) => handleSmtpChange('to_email', e.target.value)}
                      placeholder="admin@yourdomain.com"
                      className="input-neon"
                      data-testid="smtp-to-email-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">CC Recipients</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(settings?.smtp?.cc_emails || []).map((email) => (
                      <span key={email} className="px-3 py-1 bg-white/10 rounded-full text-sm flex items-center gap-2">
                        {email}
                        <button onClick={() => handleRemoveCcEmail(email)} className="hover:text-red-400">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={newCcEmail}
                      onChange={(e) => setNewCcEmail(e.target.value)}
                      placeholder="cc@example.com"
                      className="input-neon"
                      data-testid="smtp-cc-input"
                    />
                    <Button onClick={handleAddCcEmail} disabled={!newCcEmail} className="btn-outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Add CC recipients to monitor incoming emails.
                  </p>
                </div>

                <div className="flex gap-3 pt-4 border-t border-white/10">
                  <Button
                    onClick={handleTestSmtp}
                    disabled={testingSmtp}
                    variant="outline"
                    className="btn-outline"
                    data-testid="test-smtp-btn"
                  >
                    {testingSmtp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Test Connection
                  </Button>
                  <Button
                    onClick={handleSendTestEmail}
                    disabled={sendingTest}
                    className="btn-primary"
                    data-testid="send-test-email-btn"
                  >
                    {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Test Email
                  </Button>
                </div>
              </>
            )}

            {!settings?.smtp?.enabled && (
              <div className="p-6 bg-white/5 rounded-lg text-center">
                <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Enable email notifications to receive contact form submissions directly in your inbox.
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Social Links */}
        <TabsContent value="social" className="space-y-6">
          <div className="admin-card space-y-6">
            <h2 className="text-xl font-heading font-semibold">Social Links</h2>
            
            <div className="space-y-3">
              {settings?.social_links && Object.entries(settings.social_links).map(([platform, url]) => (
                <div key={platform} className="flex items-center gap-3">
                  <div className="w-28 px-3 py-2 bg-white/5 rounded-lg text-sm capitalize">
                    {platform}
                  </div>
                  <Input
                    value={url}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        social_links: { ...settings.social_links, [platform]: e.target.value },
                      })
                    }
                    className="input-neon flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveSocialLink(platform)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex items-end gap-3 pt-4 border-t border-white/10">
              <div className="form-group flex-1">
                <label className="form-label">Platform</label>
                <Input
                  value={newSocialPlatform}
                  onChange={(e) => setNewSocialPlatform(e.target.value)}
                  placeholder="twitter, linkedin, github..."
                  className="input-neon"
                  data-testid="new-social-platform-input"
                />
              </div>
              <div className="form-group flex-1">
                <label className="form-label">URL</label>
                <Input
                  value={newSocialUrl}
                  onChange={(e) => setNewSocialUrl(e.target.value)}
                  placeholder="https://..."
                  className="input-neon"
                  data-testid="new-social-url-input"
                />
              </div>
              <Button
                onClick={handleAddSocialLink}
                disabled={!newSocialPlatform || !newSocialUrl}
                className="btn-outline"
                data-testid="add-social-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsManager;
